import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faSearch, faUser, faSignOutAlt, faHome,
  faHistory, faCog, faMoneyBillWave, faUsers, faClock,
  faBell
} from '@fortawesome/free-solid-svg-icons';
import { FaFolderOpen, FaCalendarAlt, FaUserTie, FaMapMarkedAlt } from 'react-icons/fa';
import pb from '../api/pocketbase';

const ChargeStructurePage = () => {
  const navigate = useNavigate();
  const [montant, setMontant] = useState('');
  const [rubrique, setRubrique] = useState('');
  const [description, setDescription] = useState('');
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeNotifTab, setActiveNotifTab] = useState('validées');
  const [notifications, setNotifications] = useState({
    validees: [],
    attentes: []
  });
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Check authentication on mount and on popstate (back/forward navigation)
  useEffect(() => {
    const checkAuth = () => {
      // Only redirect if explicitly not logged in, not on every render
      if (pb.authStore.isValid === false) {
        navigate('/login', { replace: true });
      }
    };

    // Only check on mount and popstate, not on every render
    checkAuth();

    // Listen to popstate event to detect back/forward navigation
    window.addEventListener('popstate', checkAuth);

    return () => {
      window.removeEventListener('popstate', checkAuth);
    };
  }, [navigate]);

  // Prevent back navigation after logout
  useEffect(() => {
    const handlePopState = (event) => {
      if (!pb.authStore.isValid) {
        event.preventDefault();
        window.history.pushState(null, null, '/login');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleLogout = () => {
    // Clear authentication
    pb.authStore.clear();
    
    // Clear all local storage and session storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear any cached data
    window.location.replace('/login');
    
    // Prevent back navigation
    window.history.pushState(null, null, '/login');
    window.onpopstate = function () {
      window.history.pushState(null, null, '/login');
    };
  };

  const chargesRubriques = [
    'ABONNEMENT',
    'TRANSPORT',
    'IMPOTS ET TAXES',
    'CHARGES PERSONNEL',
    'FRAIS DE RECEPTION',
    'FOURNITURES DE BUREAU',
    'SOUS-TRAITANCE GÉNÉRALE',
    'LOCATIONS ET CHARGES LOCATIVES',
    'REDEVANCES DE LOCATION ACQUISITION',
    'ENTRETIEN, RÉPARATIONS REMISE EN ÉTAT ET MAINTENANCE',
    'PRIMES D\'ASSURANCE',
    'ÉTUDES, RECHERCHES ET DOCUMENTATION',
    'PUBLICITÉ, PUBLICATIONS, RELATIONS PUBLIQUES',
    'FRAIS DE TÉLÉCOMMUNICATIONS',
    'RÉMUNÉRATIONS D\'INTERMÉDIAIRES ET DE CONSEILS',
    'FRAIS DE FORMATION DU PERSONNEL',
    'REDEVANCES POUR BREVETS, LICENCES, LOGICIELS, CONCESSIONS ET DROITS ET VALEURS SIMILAIRES',
    'RÉMUNÉRATIONS DE PERSONNEL EXTÉRIEUR À L\'ENTITÉ',
    'ANNULATION CLIENT',
    'SAV PROJET',
    'SYNDIC',
    'AUTRES CHARGES EXTERNES'
  ];

  // Charger les charges existantes depuis PocketBase au chargement du composant
  const [viewedChargeUpdates, setViewedChargeUpdates] = useState(() =>
    JSON.parse(localStorage.getItem("viewedChargeUpdates_CG") || "[]")
  );

  // Fonction pour marquer une charge modifiée comme vue
  const markChargeUpdateAsViewed = (chargeId) => {
    const updateKey = `chargeMEA-${chargeId}`;
    if (!viewedChargeUpdates.includes(updateKey)) {
      const newViewed = [...viewedChargeUpdates, updateKey];
      setViewedChargeUpdates(newViewed);
      localStorage.setItem("viewedChargeUpdates_CG", JSON.stringify(newViewed));
      pb.collection('chargemea').update(chargeId, {
        hasNewUpdate: false,
        viewedByCG: true,
        dateViewedByCG: new Date().toISOString()
      }).catch(error => {
        console.error("Erreur lors du marquage de la charge comme vue:", error);
      });
    }
  };

  // Wrapper safe pour unsubscribe (ignore auth mismatch 403)
  const safeUnsubscribe = async (collectionName) => {
    try {
      await pb.collection(collectionName).unsubscribe();
    } catch (error) {
      if (error?.status === 403) {
        console.debug(`Auth mismatch on unsubscribe ${collectionName}, ignoring`);
      } else {
        console.error(`Error unsubscribing from ${collectionName}:`, error);
      }
    }
  };

  useEffect(() => {
    const fetchCharges = async () => {
      setLoading(true);
      try {
        // Toujours récupérer les charges validées par le DG pour l'historique
        const records = await pb.collection('chargesValideesDG').getFullList({ 
          sort: '-created',
          requestKey: 'fetchChargesHistory'
        });
        setCharges(records);
      } catch (error) {
        console.error('Erreur de chargement:', error);
      } finally {
        setLoading(false);
      }
    };

    const subscribeToCharges = async () => {
      if (!pb.authStore.isValid) return;
      
      try {
        // S'abonner aux changements en temps réel sur chargesValideesDG
        await pb.collection('chargesValideesDG').subscribe('*', function (e) {
          // Quand une nouvelle charge validée par le DG arrive, refetch les charges
          pb.collection('chargesValideesDG').getFullList({ 
            sort: '-created',
            requestKey: 'fetchChargesHistoryRT'
          }).then(records => {
            setCharges(records);
          }).catch(error => {
            console.error('Erreur lors de la mise à jour temps réel:', error);
          });
        });
      } catch (error) {
        console.debug('Subscription charges skipped:', error?.message);
      }
    };

    fetchCharges();

    return () => {};
  }, []);

  // Charger les notifications
  useEffect(() => {
  const fetchNotifications = async () => {
    try {
      const hiddenIds = JSON.parse(localStorage.getItem('hiddenNotifications') || '[]');
      // Récupérer les notifications validées
      const valides = await pb.collection('chargesValideesDG').getFullList({
        sort: '-created'
      });
      // Récupérer les notifications en attente
      const attentes = await pb.collection('chargemea').getFullList({
        filter: 'meaPar = "Controleur de Gestion" && (statut = "en attente" || statut = "")',
        sort: '-created'
      });

      setNotifications({
        validees: valides.filter(n => !hiddenIds.includes(n.id)).map(n => ({ ...n, type: 'valideeDG' })),
        attentes: attentes
          .filter(n => !hiddenIds.includes(n.id))
          .map(n => ({ ...n, type: 'attenteCG' }))
          .sort((a, b) => (b.hasNewUpdate ? 1 : 0) - (a.hasNewUpdate ? 1 : 0))
      });
    } catch (error) {
      console.error('Erreur chargement notifications:', error);
    }
  };
  fetchNotifications();

  let unsubValidees;
  let unsubAttentes;

  if (pb.authStore.isValid) {
    try {
      pb.collection('chargesValideesDG').subscribe('*', fetchNotifications)
        .then(unsub => { unsubValidees = unsub; })
        .catch(err => console.debug("Sub chargesValideesDG skipped:", err?.message));

      pb.collection('chargemea').subscribe('*', fetchNotifications)
        .then(unsub => { unsubAttentes = unsub; })
        .catch(err => console.debug("Sub chargemea skipped:", err?.message));
    } catch (err) {
      console.debug("Subscriptions skipped:", err?.message);
    }
  }

  return () => {
    if (unsubValidees) unsubValidees();
    if (unsubAttentes) unsubAttentes();
  };
}, []);

  // Formatage des dates
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      dateStyle: 'long',
      timeStyle: 'short'
    });
  };

  // Ajoute ce state pour l'édition
  const [editCharge, setEditCharge] = useState(null);

  // Modifie le formulaire pour supporter l'édition
  useEffect(() => {
    if (editCharge) {
      setMontant(editCharge.montant);
      setRubrique(editCharge.rubrique);
      setDescription(editCharge.description);
      setActiveView('dashboard');
      setShowNotifications(false);
    }
  }, [editCharge]);

  const handleEditCharge = async (notif) => {
  // Marquer comme lu en mettant à jour la notification
    if (notif.hasNewUpdate) {
      try {
        await pb.collection('chargemea').update(notif.id, { 
          hasNewUpdate: false, 
          viewedByCG: true, 
          dateViewedByCG: new Date().toISOString() 
        });
      } catch (error) {
        console.error("Erreur lors de la mise à jour de la notification:", error);
      }
    }
  // Remplir le formulaire pour l'édition
  setEditCharge(notif);
  };

  // Modifie handleSubmit pour gérer l'édition
  // ...existing code...
// ...existing code...
const handleSubmit = async (e) => {
  e.preventDefault();
  if (!montant || !rubrique || !description) {
    alert('Veuillez remplir tous les champs');
    return;
  }

  try {
    setLoading(true);
    if (editCharge) {
      // Mise à jour dans chargemea
      await pb.collection('chargemea').update(editCharge.id, {
        montant: Number(montant),
        rubrique,
        sousRubrique: '',
        description,
        statut: 'modifiée',
        hasNewUpdate: true,
        updateCount: (editCharge.updateCount || 0) + 1,
        dateModification: new Date().toISOString(),
        viewedByCG: false,
        meaPar: "Controleur de Gestion",
      });
      // Création dans chargesStructure
      const newCharge = {
        montant: Number(montant),
        rubrique,
        sousRubrique: '',
        description,
        dateCreation: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        statut: 'en attente',
      };
      await pb.collection('chargesStructure').create(newCharge);
      // Masquer la notification localement
      const hiddenIds = JSON.parse(localStorage.getItem('hiddenNotifications') || '[]');
      if (!hiddenIds.includes(editCharge.id)) {
        hiddenIds.push(editCharge.id);
        localStorage.setItem('hiddenNotifications', JSON.stringify(hiddenIds));
      }
      alert('Charge modifiée et renvoyée avec succès');
      setEditCharge(null);
    } 
    else {
      // Création normale - SEULEMENT dans chargesStructure
      const newCharge = {
        montant: Number(montant),
        rubrique,
        sousRubrique: '',
        description,
        dateCreation: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        statut: 'en attente',
      };
      await pb.collection('chargesStructure').create(newCharge);
      alert('Charge enregistrée avec succès');
    }
    // Réinitialisation du formulaire
    setMontant('');
    setRubrique('');
    setDescription('');
  } catch (error) {
    console.error('Erreur:', error);
    alert('Erreur lors de l\'opération');
  } finally {
    setLoading(false);
  }
};


  const handleDelete = (chargeId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette charge ?')) {
      setCharges(prevCharges => prevCharges.filter(charge => charge.id !== chargeId));
      alert('Charge supprimée avec succès!');
    }
  };

  // Fonction pour masquer une notification (persistant)
  const hideNotification = (notif, type) => {
    const hiddenIds = JSON.parse(localStorage.getItem('hiddenNotifications') || '[]');
    if (!hiddenIds.includes(notif.id)) {
      hiddenIds.push(notif.id);
      localStorage.setItem('hiddenNotifications', JSON.stringify(hiddenIds));
    }
    if (type === "validées") {
      setNotifications(prev => ({
        ...prev,
        validees: prev.validees.filter(n => n.id !== notif.id)
      }));
    } else {
      setNotifications(prev => ({
        ...prev,
        attentes: prev.attentes.filter(n => n.id !== notif.id)
      }));
    }
  };

  // Calculer les totaux par rubrique
  const totauxParRubrique = charges.reduce((acc, charge) => {
    acc[charge.rubrique] = (acc[charge.rubrique] || 0) + Number(charge.montant);
    return acc;
  }, {});

  const newAttenteCount = notifications.attentes.filter(n => n.hasNewUpdate).length;

  // Composant pour afficher les notifications
  const NotificationsModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Notifications des charges</h3>
          <button
            onClick={() => setShowNotifications(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setActiveNotifTab("validées")}
              className={`px-4 py-2 rounded flex items-center gap-2 ${
                activeNotifTab === "validées"
                  ? "bg-green-600 text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              Charges validées
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                activeNotifTab === "validées"
                  ? "bg-white text-green-600"
                  : "bg-green-200 text-green-700"
              }`}>
              {notifications.validees.length}
              </span>
            </button>
            <button
              onClick={() => setActiveNotifTab("attentes")}
              className={`px-4 py-2 rounded flex items-center gap-2 ${
                activeNotifTab === "attentes"
                  ? "bg-yellow-600 text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              Mises en attente
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                activeNotifTab === "attentes"
                  ? "bg-white text-yellow-600"
                  : "bg-yellow-200 text-yellow-700"
              }`}>
              {notifications.attentes.length}
              </span>
            </button>
          </div>
          {activeNotifTab === "validées" ? (
            notifications.validees.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <span className="text-gray-400 text-4xl mb-3">🔔</span>
                <p className="text-gray-600">Aucune notification de validation</p>
              </div>
            ) : (
              notifications.validees.map((notif) => (
                <div
                  key={notif.id}
                  className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500 animate-fade-in flex justify-between items-start mb-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-green-500">✓</span>
                      <span className="font-semibold">
                        Validé par Directeur Général
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      Date : {notif.dateValidationDG?.toDate
                        ? notif.dateValidationDG.toDate().toLocaleString()
                        : notif.dateValidationDG
                          ? new Date(notif.dateValidationDG).toLocaleString()
                          : ""}
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="font-medium">{notif.rubrique}</div>
                      <div className="text-sm text-gray-600">
                        Montant : {notif.montant?.toLocaleString()} FCFA
                      </div>
                      {notif.description && (
                        <div className="mt-1 text-sm text-gray-700">
                          Description : {notif.description}
                        </div>
                      )}
                    </div>
                    <div className="mt-3">
                      <button
                        className="text-red-600 hover:underline text-sm"
                        onClick={() => hideNotification(notif, "validées")}
                      >
                        Supprimer la notification
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )
          ) : (
            notifications.attentes.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <span className="text-gray-400 text-4xl mb-3">🔔</span>
                <p className="text-gray-600">Aucune charge en attente</p>
              </div>
            ) : (
              notifications.attentes.map((notif) => (
                <div
                  key={notif.id}
                  className="bg-white rounded-lg shadow-md p-4 border-l-4 border-yellow-500 animate-fade-in flex justify-between items-start mb-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-yellow-500">⏳</span>
                      <span className="font-semibold text-yellow-700">
                        Mise en attente par Contrôleur de Gestion
                      </span>
                      {notif.hasNewUpdate && (
                        <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                          NOUVEAU
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      Date : {notif.datemea
                        ? new Date(notif.datemea).toLocaleString()
                        : ""}
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="font-medium">{notif.rubrique}</div>
                      <div className="text-sm text-gray-600">
                        Montant : {notif.montant?.toLocaleString()} FCFA
                      </div>
                      {notif.description && (
                        <div className="mt-1 text-sm text-gray-700">
                          Description : {notif.description}
                        </div>
                      )}
                      {/* Affichage du motif */}
                      {notif.motif && (
                        <div className="mt-1 text-sm text-red-600 font-semibold">
                          Motif : {notif.motif}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 flex gap-3">
                      <button
                        className="text-blue-600 hover:underline text-sm"
                        onClick={() => handleEditCharge(notif)}
                      >
                        Modifier et renvoyer
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-200 text-gray-900 min-h-screen flex flex-col animate-fade-in">
      {/* HEADER */}
      <div className="fixed top-0 left-0 right-0 z-50 flex bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 h-14 items-center justify-between">
        <div className="flex items-center space-x-4 min-w-0">
          <img src="dywanes-logo.png" alt="Dywane Logo" className="h-10 w-auto" />
        </div>
        <div className="flex-1 max-w-lg mx-4">
          <div className="relative text-gray-400 focus-within:text-gray-600">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input type="search" placeholder="Rechercher..." className="block w-full rounded-md border border-gray-300 bg-gray-100 py-1.5 pl-10 pr-3 text-sm placeholder-gray-400 focus:border-gray-400 focus:ring-0 focus:outline-none" />
          </div>
        </div>
        <div className="flex items-center space-x-2 min-w-0">
          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-600 text-sm select-none">
            <FontAwesomeIcon icon={faUser} />
          </div>
          <span className="text-sm truncate min-w-0">controleur</span>
          <button className="text-gray-500 hover:text-gray-700 focus:outline-none">
            <FontAwesomeIcon icon={faSignOutAlt} />
          </button>
        </div>
      </div>

      {/* BODY */}
      <div className="flex flex-1 overflow-hidden">
        {/* SIDEBAR */}
        <nav className="fixed top-14 left-0 z-40 h-[calc(100vh-3.5rem)] bg-gray-900 w-56 flex-shrink-0 px-4 py-6 flex flex-col space-y-6 text-gray-300 text-sm select-none animate-slide-in-left">
          <button 
            onClick={() => setActiveView('dashboard')}
            className={`flex items-center space-x-2 w-full rounded-md px-4 py-2 transition-colors ${
              activeView === 'dashboard' ? 'bg-blue-800 text-white' : 'hover:bg-gray-800'
            }`}
          >
            <FontAwesomeIcon icon={faHome} /><span>Tableau de bord</span>
          </button>

          <button 
            onClick={() => setActiveView('history')}
            className={`flex items-center space-x-2 w-full rounded-md px-4 py-2 transition-colors ${
              activeView === 'history' ? 'bg-blue-800 text-white' : 'hover:bg-gray-800'
            }`}
          >
            <FontAwesomeIcon icon={faHistory} /><span>Historique</span>
          </button>

          {/* Ajouter le bouton de notifications ici */}
          <button
            onClick={() => setShowNotifications(true)}
            className={`flex items-center space-x-2 w-full rounded-md px-4 py-2 transition-colors hover:bg-gray-800 relative`}
          >
            <FontAwesomeIcon icon={faBell} />
            <span>Notifications</span>
            {newAttenteCount > 0 && (
              <span className="absolute top-1 right-2 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                {newAttenteCount}
              </span>
            )}
          </button>

          <div className="flex-grow" />
          <div className="border-t border-gray-700 pt-3">
            <a href="#" className="flex items-center space-x-2 hover:text-white">
              <FontAwesomeIcon icon={faCog} /><span>Paramètres</span>
            </a>
            <button 
              onClick={() => setShowLogoutModal(true)}
              className="flex items-center space-x-2 mt-4 hover:text-white w-full text-left"
            >
              <FontAwesomeIcon icon={faSignOutAlt} /><span>Déconnexion</span>
            </button>
          </div>
        </nav>

        {/* MAIN CONTENT */}
        <main className="flex-1 overflow-auto p-6 pl-60 pt-20 animate-slide-in-right">
          <div className="space-y-6">
            {/* Header commun */}
            <div className="bg-white shadow mb-4"> {/* Ajout de mb-4 pour réduire l'espace */}
              <div className="px-4 sm:px-6 lg:px-8 py-3"> {/* Réduction du py-4 à py-3 */}
                <div className="flex justify-between items-center">
                  <h1 className="text-2xl font-semibold text-gray-900 flex items-center">
                    <FontAwesomeIcon icon={faBuilding} className="mr-3 text-blue-600" />
                    {activeView === 'dashboard' ? 'Gestion des Charges de Structure' : 'Historique des Charges'}
                  </h1>
                </div>
              </div>
            </div>

            {/* Contenu conditionnel */}
            {activeView === 'dashboard' ? (
              // Vue Tableau de bord
              <div className="px-4 sm:px-6 lg:px-8 py-4 space-y-4"> {/* Réduction du py-8 à py-4 et space-y-6 à space-y-4 */}
                {/* Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-75">Total des Charges</p>
                        <p className="text-2xl font-bold mt-2">
                          {charges.reduce((sum, charge) => sum + Number(charge.montant), 0).toLocaleString()} FCFA
                        </p>
                      </div>
                      <FontAwesomeIcon icon={faMoneyBillWave} className="text-3xl opacity-75" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-75">Nombre de Charges</p>
                        <p className="text-2xl font-bold mt-2">{charges.length}</p>
                      </div>
                      <FontAwesomeIcon icon={faUsers} className="text-3xl opacity-75" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-75">Dernière mise à jour</p>
                        <p className="text-2xl font-bold mt-2">
                          {charges[0]?.dateCreation
                            ? new Date(charges[0].dateCreation).toLocaleDateString('fr-FR')
                            : 'N/A'}
                        </p>
                      </div>
                      <FontAwesomeIcon icon={faClock} className="text-3xl opacity-75" />
                    </div>
                  </div>
                </div>

                {/* Formulaire */}
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {editCharge ? 'Modifier la Charge de Structure' : 'Nouvelle Charge de Structure'}
                    </h2>
                  </div>
                  <div className="p-6">
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Montant</label>
                        <input
                          type="number"
                          value={montant}
                          onChange={(e) => setMontant(e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Entrez le montant en FCFA"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Rubrique</label>
                        <select
                          value={rubrique}
                          onChange={(e) => setRubrique(e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Sélectionnez une rubrique</option>
                          {chargesRubriques.map((rub) => (
                            <option key={rub} value={rub}>{rub}</option>
                          ))}
                        </select>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          rows="3"
                          placeholder="Décrivez la charge..."
                        />
                      </div>

                      <div className="md:col-span-2 flex justify-end gap-3">
                        {editCharge && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditCharge(null);
                              setMontant('');
                              setRubrique('');
                              setDescription('');
                            }}
                            className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors duration-200"
                          >
                            Annuler
                          </button>
                        )}
                        <button
                          type="submit"
                          disabled={loading}
                          className={`bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200 ${
                            loading ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {loading 
                            ? 'Enregistrement...' 
                            : editCharge 
                              ? 'Modifier et renvoyer' 
                              : 'Enregistrer la charge'
                          }
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            ) : (
              // Vue Historique
              <div className="px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-white rounded-xl shadow-xl overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                        <FontAwesomeIcon icon={faHistory} className="mr-2 text-blue-600" />
                        Historique des Charges de Structure
                      </h2>
                      <div className="flex gap-4">
                        <span className="text-sm text-gray-500">
                          Total: {charges.length} charge(s)
                        </span>
                        <span className="text-sm font-semibold text-green-600">
                          Montant Total: {charges.reduce((sum, charge) => sum + (Number(charge.montant) || 0), 0).toLocaleString('fr-FR')} FCFA
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Date
                          </th>
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Rubrique
                          </th>
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Description
                          </th>
                          <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Montant
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {charges.map((charge, index) => (
                          <tr key={charge.id} className={`
                            transition-colors hover:bg-blue-50
                            ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                          `}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {charge.created
                                ? new Date(charge.created).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })
                                : 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                  {charge.rubrique}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 line-clamp-2">
                                {charge.description}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="text-sm font-semibold text-gray-900">
                                <span className="font-mono">{Number(charge.montant).toLocaleString()} FCFA</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {charges.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <FontAwesomeIcon icon={faHistory} className="text-4xl mb-3 opacity-50" />
                      <p>Aucune charge enregistrée pour le moment</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modal des notifications ajouté ici */}
      {showNotifications && <NotificationsModal />}

      {/* Modal de déconnexion */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 bg-gray-50 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Confirmation de déconnexion</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-6">Êtes-vous sûr de vouloir vous déconnecter ?</p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Oui, se déconnecter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default ChargeStructurePage;
